'use client';

import { cn } from '@core/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, ReactElement } from 'react';
import React, { createContext, useContext } from 'react';

type TabContextType<T extends string = string> = {
  activeValue: T;
  onChange?: (value: T) => void;
  size?: 'sm' | 'lg';
  variant?: 'full' | 'flex';
};

const TabContext = createContext<TabContextType<string> | undefined>(undefined);

const useTabContext = <T extends string = string>() => {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTabContext must be used within a TabProvider');
  }

  return context as unknown as TabContextType<T>;
};

const tabItemVariants = cva(
  'relative flex shrink-0 items-center justify-center gap-4 whitespace-nowrap bg-transparent transition-colors',
  {
    variants: {
      size: {
        sm: 'h-53 min-w-60 px-16 typo-body4',
        lg: 'h-70 min-w-120 px-20 typo-body1',
      },
      variant: {
        full: 'flex-1',
        flex: '',
      },
      active: {
        true: 'font-700 text-Gray-black before:absolute before:inset-x-0 before:bottom-0 before:z-10 before:border-b-Gray-900 before:transition-none',
        false: 'font-400 text-Gray-900 hover:text-Gray-600',
      },
      disabled: {
        true: 'cursor-not-allowed opacity-50',
        false: 'cursor-pointer',
      },
    },
    compoundVariants: [
      {
        size: 'sm',
        active: true,
        className: 'before:border-b-[3px]',
      },
      {
        size: 'lg',
        active: true,
        className: 'before:border-b-4',
      },
    ],
    defaultVariants: {
      size: 'sm',
      variant: 'full',
      active: false,
      disabled: false,
    },
  },
);

interface TabItemProps extends VariantProps<typeof tabItemVariants> {
  value: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

function TabItem({
  value,
  icon,
  disabled = false,
  className,
  children,
}: TabItemProps) {
  const { activeValue, onChange, size, variant } = useTabContext();
  const isActive = activeValue === value;

  const handleClick = () => {
    if (!disabled) {
      onChange?.(value);
    }
  };

  return (
    <button
      type="button"
      className={cn(
        tabItemVariants({
          size,
          variant,
          active: isActive,
          disabled,
          className,
        }),
      )}
      onClick={handleClick}
      disabled={disabled}
      aria-selected={isActive}
      role="tab"
    >
      {children}
      {icon}
    </button>
  );
}

const tabVariants = cva(
  'relative overflow-x-auto overflow-y-hidden border-b border-Gray-300 hide-scrollbar',
  {
    variants: {
      size: {
        sm: 'h-53',
        lg: 'h-70',
      },
      variant: {
        full: 'flex',
        flex: 'flex',
      },
    },
    defaultVariants: {
      size: 'sm',
      variant: 'full',
    },
  },
);

interface TabProps<T extends string = string>
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>,
    VariantProps<typeof tabVariants> {
  value: T;
  onChange?: (value: T) => void;
  size?: 'sm' | 'lg';
  variant?: 'full' | 'flex';
  children: React.ReactNode;
  className?: string;
}

type TabComponent = {
  <T extends string = string>(props: TabProps<T>): ReactElement;
  Item: <V extends string>(
    props: Omit<TabItemProps, 'value'> & { value: V },
  ) => ReactElement;
};

function TabBase<T extends string = string>({
  value,
  onChange,
  size = 'sm',
  variant = 'full',
  children,
  className,
  ...props
}: TabProps<T>) {
  const handleChange = (newValue: string) => {
    onChange?.(newValue as T);
  };

  return (
    <TabContext.Provider
      value={{
        activeValue: value,
        onChange: handleChange,
        size,
        variant,
      }}
    >
      <div
        className={cn(
          tabVariants({
            size,
            variant,
            className,
          }),
        )}
        role="tablist"
        {...props}
      >
        {children}
      </div>
    </TabContext.Provider>
  );
}

export const Tab = TabBase as unknown as TabComponent;
Tab.Item = TabItem;

export type { TabComponent, TabContextType, TabItemProps, TabProps };
export { useTabContext };
